import { useEffect, useState } from 'react';
import Front from '@frontapp/plugin-sdk';

/**
 * Types minimalistes du SDK Front.
 * On ne s'appuie volontairement que sur les champs utilises ici, afin de rester
 * compatible avec les evolutions du SDK.
 */
export interface FrontTeammate {
  id?: string;
  name?: string;
  email?: string;
  username?: string;
}

export interface FrontContext {
  type: 'singleConversation' | 'noConversation' | 'multiConversations' | 'messageComposer';
  teammate?: FrontTeammate;
  openUrl?: (url: string) => void;
}

/**
 * S'abonne au contexte Front.
 *
 * Le panneau n'exploite plus la conversation : seule compte l'identite du
 * collaborateur connecte, pour lui montrer sa propre quarantaine. Le contexte
 * reste utile pour `teammate` et `openUrl`.
 */
export function useFrontContext(): FrontContext | undefined {
  const [context, setContext] = useState<FrontContext>();

  useEffect(() => {
    const subscription = Front.contextUpdates.subscribe((next) => {
      setContext(next as unknown as FrontContext);
    });
    return () => subscription.unsubscribe();
  }, []);

  return context;
}

const STORAGE_KEY = 'mib.mailboxes';

function isEmail(value: string): boolean {
  return /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(value);
}

export function normalizeAddress(value: string): string {
  return value.trim().toLowerCase();
}

/** Adresses saisies manuellement par l'utilisateur, conservees dans ce navigateur. */
export function readOverrides(): string[] {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed.map((value) => normalizeAddress(String(value))).filter(isEmail);
  } catch {
    return [];
  }
}

export function writeOverrides(addresses: string[]): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(addresses));
  } catch {
    // Stockage indisponible (navigation privee, iframe restreinte) : on continue
    // sans persistance plutot que d'echouer.
  }
}

/**
 * Boites proposees dans la liste deroulante.
 *
 * L'adresse du compte Front vient toujours en tete : les adresses ajoutees
 * manuellement s'y ajoutent, elles ne la remplacent pas. Un collaborateur qui
 * gere une boite partagee garde ainsi acces a la sienne.
 */
export function resolveMailboxes(teammate: FrontTeammate | undefined, overrides: string[]): string[] {
  const email = normalizeAddress(teammate?.email ?? '');
  const all = isEmail(email) ? [email, ...overrides] : [...overrides];
  return [...new Set(all)];
}

export function formatDate(millis: number | null): string {
  if (!millis) return '—';
  return new Date(millis).toLocaleString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}
